import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/villa142`;
const TOKEN_KEY = "villa142_token";
const ADMIN_TOKEN_KEY = "villa142_admin_token";
const QUEUE_KEY = "villa142_pending_v2";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

export async function clientAuth(password) {
  const { data } = await axios.post(`${API}/auth`, { password });
  localStorage.setItem(TOKEN_KEY, data.token);
  return data;
}

export async function fetchProject() {
  const { data } = await axios.get(`${API}/project`, { headers: authHeader() });
  return data;
}

export function savePrefs(prefs) {
  return axios.put(`${API}/prefs`, prefs, { headers: authHeader() }).catch(() => {});
}

export async function submitReflections() {
  const { data } = await axios.post(`${API}/submit`, {}, { headers: authHeader() });
  return data;
}

export function deleteEntry(key, contributor) {
  delete knownRev[`${key}::${contributor}`];
  return axios.delete(`${API}/entries/${encodeURIComponent(key)}/${encodeURIComponent(contributor)}`, { headers: authHeader() });
}

/* ---------- per-response revision (optimistic concurrency) ---------- */

const knownRev = {};
const revId = (key, contributor) => `${key}::${contributor}`;

export function setEntryRevs(entries) {
  entries.forEach((e) => { knownRev[revId(e.key, e.contributor)] = e.rev ?? 0; });
}

export function setEntryRev(key, contributor, rev) {
  if (rev == null) return;
  knownRev[revId(key, contributor)] = rev;
}

/* ---------- resilient entry save queue ---------- */

let queue = {};
let flushTimer = null;
let retryTimer = null;
let flushing = false;
let statusCb = () => {};
let authFailCb = () => {};
let conflictCb = () => {};

const persistQueue = () => localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));

export function initQueue(onStatus, onAuthFail, onConflict) {
  statusCb = onStatus;
  authFailCb = onAuthFail;
  conflictCb = onConflict || (() => {});
  try {
    queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || "{}");
  } catch {
    queue = {};
  }
  if (Object.keys(queue).length) scheduleFlush(400);
  window.addEventListener("online", () => scheduleFlush(300));
}

export function queueEntrySave(key, contributor, patch) {
  const id = `${key}::${contributor}`;
  queue[id] = { ...(queue[id] || {}), ...patch };
  persistQueue();
  statusCb("saving");
  scheduleFlush(900);
}

export function clearPending(key, contributor) {
  delete queue[`${key}::${contributor}`];
  persistQueue();
}

export function pendingQuestionKeys() {
  return new Set(Object.keys(queue).map((id) => id.slice(0, id.indexOf("::"))));
}

export const hasPendingFor = (key, contributor) => Boolean(queue[`${key}::${contributor}`]);

function scheduleFlush(ms) {
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, ms);
}

async function flush() {
  if (flushing) {
    scheduleFlush(500);
    return { ok: false, reason: "busy" };
  }
  const ids = Object.keys(queue);
  if (!ids.length) return { ok: true };
  flushing = true;
  clearTimeout(retryTimer);
  statusCb("saving");
  let conflicted = false;
  try {
    for (const id of ids) {
      const sep = id.indexOf("::");
      const key = id.slice(0, sep);
      const contributor = id.slice(sep + 2);
      const snap = queue[id];
      if (!snap) continue;
      const body = { key, contributor, ...snap, base_rev: knownRev[id] ?? null };
      try {
        const { data } = await axios.put(`${API}/entries`, body, { headers: authHeader(), timeout: 20000 });
        if (data && data.rev != null) knownRev[id] = data.rev;
        if (queue[id] === snap) delete queue[id];
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        conflicted = true;
        delete queue[id];
        conflictCb(key, contributor, e.response.data?.detail?.latest || null);
      }
      persistQueue();
    }
    if (Object.keys(queue).length) {
      scheduleFlush(900);
      statusCb("saving");
    } else {
      statusCb(conflicted ? "idle" : "saved");
    }
    return conflicted ? { ok: false, reason: "conflict" } : { ok: true };
  } catch (e) {
    if (e.response?.status === 401) {
      authFailCb();
      return { ok: false, reason: "auth" };
    }
    statusCb("pending");
    retryTimer = setTimeout(flush, 4000);
    return { ok: false, reason: "network" };
  } finally {
    flushing = false;
  }
}

export async function flushNow() {
  clearTimeout(flushTimer);
  if (flushing) {
    await new Promise((r) => setTimeout(r, 250));
    return flushNow();
  }
  return flush();
}

export const hasPending = () => Object.keys(queue).length > 0;

/* ---------- media ---------- */

export function mediaUrl(id, token, variant) {
  const v = variant ? `&variant=${encodeURIComponent(variant)}` : "";
  return `${API}/media/${id}/file?t=${encodeURIComponent(token || getToken() || "")}${v}`;
}

export function studyUrl(id, token) {
  return `${API}/studies/${id}/file?t=${encodeURIComponent(token || getToken() || "")}`;
}

export async function fetchQuestionnaire() {
  const { data } = await axios.get(`${API}/questionnaire`, { headers: authHeader() });
  return data;
}

let _uploadSettings = null;
export async function fetchUploadSettings() {
  if (_uploadSettings) return _uploadSettings;
  _uploadSettings = axios
    .get(`${API}/settings`, { headers: authHeader() })
    .then((r) => r.data)
    .catch(() => null);
  return _uploadSettings;
}

export async function uploadMedia(key, contributor, kind, blob, filename, caption = "", itemId = "", duration = "") {
  const form = new FormData();
  form.append("file", blob, filename);
  form.append("key", key);
  form.append("contributor", contributor);
  form.append("kind", kind);
  form.append("caption", caption);
  form.append("item_id", itemId);
  form.append("duration", duration === "" ? "" : String(duration));
  const { data } = await axios.post(`${API}/media`, form, { headers: authHeader() });
  setEntryRev(key, contributor, data.entry_rev);
  return data;
}

export async function deleteMedia(id) {
  const { data } = await axios.delete(`${API}/media/${id}`, { headers: authHeader() });
  if (data?.key) setEntryRev(data.key, data.contributor, data.entry_rev);
  return data;
}

export function updateMediaCaption(id, caption) {
  return axios.put(`${API}/media/${id}`, { caption }, { headers: authHeader() }).catch(() => {});
}

/* ---------- studio / admin ---------- */

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);
const adminHeader = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export async function adminAuth(password) {
  const { data } = await axios.post(`${API}/admin/auth`, { password });
  localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
}

export async function fetchAdminProject() {
  const { data } = await axios.get(`${API}/admin/project`, { headers: adminHeader() });
  return data;
}

export function saveFollowup(entryId, note) {
  return axios.put(`${API}/admin/entries/${entryId}/followup`, { note }, { headers: adminHeader() });
}

/* ---------- studio: contributors ---------- */

export async function fetchContributors() {
  const { data } = await axios.get(`${API}/admin/contributors`, { headers: adminHeader() });
  return data;
}

export async function addContributor(name) {
  const { data } = await axios.post(`${API}/admin/contributors`, { name }, { headers: adminHeader() });
  return data;
}

export function updateContributor(cid, patch) {
  return axios.put(`${API}/admin/contributors/${cid}`, patch, { headers: adminHeader() });
}

export function deleteContributor(cid) {
  return axios.delete(`${API}/admin/contributors/${cid}`, { headers: adminHeader() });
}

/* ---------- studio: voice-note exports ---------- */

export function adminDeleteMedia(mid) {
  return axios.delete(`${API}/admin/media/${mid}`, { headers: adminHeader() });
}

export function adminMediaDownloadUrl(mid) {
  return `${API}/admin/media/${mid}/download?t=${encodeURIComponent(getAdminToken() || "")}`;
}

export function adminImageDownloadUrl(mid) {
  return `${API}/admin/media/${mid}/image?t=${encodeURIComponent(getAdminToken() || "")}`;
}

export async function fetchAdminSettings() {
  const { data } = await axios.get(`${API}/admin/settings`, { headers: adminHeader() });
  return data;
}

export async function saveAdminSettings(patch) {
  const { data } = await axios.put(`${API}/admin/settings`, patch, { headers: adminHeader() });
  return data;
}

export function adminVoiceZipUrl() {
  return `${API}/admin/export/voice-notes.zip?t=${encodeURIComponent(getAdminToken() || "")}`;
}

/* ---------- brand ---------- */

export async function fetchBrand() {
  const { data } = await axios.get(`${API}/brand`);
  return data;
}

export function brandLogoUrl(cacheKey) {
  return `${API}/brand/logo${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ""}`;
}

export async function uploadBrandLogo(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/brand/logo`, form, { headers: adminHeader() });
  return data;
}

export function updateBrandAlt(alt) {
  return axios.put(`${API}/admin/brand`, { alt }, { headers: adminHeader() });
}

export function deleteBrandLogo() {
  return axios.delete(`${API}/admin/brand/logo`, { headers: adminHeader() });
}

/* ---------- welcome media ---------- */

const absUrls = (item) => {
  if (!item) return item;
  const base = process.env.REACT_APP_BACKEND_URL;
  const urls = {};
  Object.entries(item.urls || {}).forEach(([k, v]) => { urls[k] = `${base}${v}`; });
  return { ...item, urls };
};

export async function fetchWelcomeMedia(surface = "welcome") {
  const { data } = await axios.get(`${API}/welcome-media/active`, { params: { surface } });
  return absUrls(data.media);
}

export async function fetchWelcomeMediaList() {
  const { data } = await axios.get(`${API}/admin/welcome-media`, { headers: adminHeader() });
  return data.map(absUrls);
}

const CHUNK = 4 * 1024 * 1024;

export async function uploadWelcomeMedia(file, kind, title, onProgress, activate = false, surface = "welcome") {
  const { data: init } = await axios.post(`${API}/admin/welcome-media/uploads`, {}, { headers: adminHeader() });
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i += 1) {
    const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
    await axios.put(`${API}/admin/welcome-media/uploads/${init.upload_id}/chunks/${i}`, blob, {
      headers: { ...adminHeader(), "Content-Type": "application/octet-stream" },
    });
    if (onProgress) onProgress((i + 1) / total);
  }
  const { data } = await axios.post(
    `${API}/admin/welcome-media/uploads/${init.upload_id}/complete?activate=${activate ? "true" : "false"}`,
    { kind, surface, title, filename: file.name, content_type: file.type, total_chunks: total },
    { headers: adminHeader() }
  );
  return absUrls(data);
}

export async function updateWelcomeMedia(id, patch) {
  const { data } = await axios.put(`${API}/admin/welcome-media/${id}`, patch, { headers: adminHeader() });
  return absUrls(data);
}

export async function uploadWelcomePoster(id, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/welcome-media/${id}/poster`, form, { headers: adminHeader() });
  return absUrls(data);
}

export function resetWelcomePoster(id) {
  return axios.delete(`${API}/admin/welcome-media/${id}/poster`, { headers: adminHeader() });
}

export function reprocessWelcomeMedia(id) {
  return axios.post(`${API}/admin/welcome-media/${id}/reprocess`, {}, { headers: adminHeader() });
}

export function deleteWelcomeMedia(id) {
  return axios.delete(`${API}/admin/welcome-media/${id}`, { headers: adminHeader() });
}

/* ---------- studio: definition ---------- */

export async function fetchAdminDefinition() {
  const { data } = await axios.get(`${API}/admin/definition`, { headers: adminHeader() });
  return data;
}

export async function createDraft() {
  const { data } = await axios.post(`${API}/admin/definition/draft`, {}, { headers: adminHeader() });
  return data;
}

export function saveDraft(chapters) {
  return axios.put(`${API}/admin/definition/draft`, { chapters }, { headers: adminHeader() });
}

export async function publishDraft() {
  const { data } = await axios.post(`${API}/admin/definition/publish`, {}, { headers: adminHeader() });
  return data;
}

export function discardDraft() {
  return axios.delete(`${API}/admin/definition/draft`, { headers: adminHeader() });
}

/* ---------- studio: visual studies ---------- */

export async function createStudy(title) {
  const form = new FormData();
  form.append("title", title);
  const { data } = await axios.post(`${API}/admin/studies`, form, { headers: adminHeader() });
  return data;
}

export function updateStudy(id, patch) {
  return axios.put(`${API}/admin/studies/${id}`, patch, { headers: adminHeader() });
}

export async function uploadStudyAsset(id, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/studies/${id}/asset`, form, { headers: adminHeader() });
  return data;
}

export function deleteStudy(id) {
  return axios.delete(`${API}/admin/studies/${id}`, { headers: adminHeader() });
}

/* ---------- studio: scoped resets + audit ---------- */

export function adminDeleteContribution(entryId) {
  return axios.delete(`${API}/admin/entries/${entryId}`, { headers: adminHeader() });
}

export function adminDeleteQuestionResponses(qid) {
  return axios.delete(`${API}/admin/questions/${qid}/entries`, { headers: adminHeader() });
}

export function adminDeleteContributorResponses(cid) {
  return axios.delete(`${API}/admin/contributors/${cid}/entries`, { headers: adminHeader() });
}

export function adminDeleteAllResponses() {
  return axios.delete(`${API}/admin/project/responses`, { headers: adminHeader() });
}

export async function fetchAudit() {
  const { data } = await axios.get(`${API}/admin/audit`, { headers: adminHeader() });
  return data;
}
